import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const src = (u) => (u && u.startsWith("/") ? `${API_BASE}${u}` : u);

const CLASSIC_IMG = "https://images.pexels.com/photos/9510860/pexels-photo-9510860.jpeg";

const FALLBACK = [
  { id: "brand-classic", title_ar: "الورد لغة القلوب", title_en: "Where flowers speak", link: "/shop" },
  {
    id: "teacher",
    image: "https://static.prod-images.emergentagent.com/jobs/757865c7-80d0-4582-ad8c-41c9eb28bb58/images/ddba8c3a254e0294b02a74ef1e21c0c1843c61e12046368dce87b006829aafc6.jpeg",
    title_ar: "يوم المعلم", title_en: "Teacher's Day",
    subtitle_ar: "عبّر عن امتنانك بأجمل الهدايا",
    subtitle_en: "Show your gratitude with the finest gifts",
    link: "/shop",
  },
  {
    id: "ai",
    image: "https://images.unsplash.com/photo-1561181286-d3fee7d55364?w=1200&q=75&auto=format&fit=crop",
    title_ar: "صمّم باقتك بالذكاء", title_en: "Design with AI",
    subtitle_ar: "اوصف الباقة والذكاء يصمّمها لك بثوانٍ",
    subtitle_en: "Describe it & AI designs it in seconds",
    link: "/ai",
  },
  {
    id: "offers",
    image: "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=1200&q=75&auto=format&fit=crop",
    title_ar: "عروض اليوم", title_en: "Today's offers",
    subtitle_ar: "خصومات على باقات وهدايا مختارة",
    subtitle_en: "Discounts on selected bouquets & gifts",
    link: "/offers",
  },
];

/* البنر الرئيسي المتحرك: الشريحة الأولى دائماً بنر الهوية الأخضر الكلاسيكي (الورد لغة القلوب)
   ثم بنرات الأدمن (صور أو فيديوهات) من /banners، وإن لم توجد تظهر الشرائح الافتراضية. */
export default function HeroCarousel() {
  const { lang, t } = useApp();
  const ar = lang === "ar";
  const [slides, setSlides] = useState(FALLBACK);
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);
  const videoRefs = useRef({});

  useEffect(() => {
    http.get("/banners").then(({ data }) => {
      if (Array.isArray(data) && data.length) {
        const admin = data.map((b) => ({ ...b, image: src(b.image), video: src(b.video) }));
        setSlides([FALLBACK[0], ...admin]);
        setIdx(0);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const isVideo = slides[idx]?.media_type === "video" && slides[idx]?.video;
    timer.current = setTimeout(() => setIdx((i) => (i + 1) % slides.length), isVideo ? 12000 : 5000);
    return () => clearTimeout(timer.current);
  }, [idx, slides]);

  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([k, v]) => {
      if (!v) return;
      if (Number(k) === idx) v.play().catch(() => {});
      else v.pause();
    });
  }, [idx]);

  const s = slides[idx] || slides[0];
  if (!s) return null;
  const classic = s.id === "brand-classic";

  return (
    <section className="relative overflow-hidden rounded-[1.5rem]" data-testid="hero-carousel">
      {/* طبقات الخلفية لكل شريحة (تبديل ناعم) */}
      {slides.map((sl, i) => {
        const isClassic = sl.id === "brand-classic";
        const isVideo = sl.media_type === "video" && sl.video;
        return (
          <div key={sl.id || i}
               className="absolute inset-0 transition-opacity duration-700"
               style={{ opacity: i === idx ? 1 : 0,
                        background: isClassic ? "var(--surface-invert)" : "#111" }}>
            {isVideo ? (
              <video ref={(el) => { videoRefs.current[i] = el; }}
                     src={sl.video} muted loop playsInline autoPlay={i === idx}
                     preload={i === 0 ? "auto" : "metadata"}
                     className="absolute inset-0 w-full h-full object-cover"
                     data-testid={`hero-video-${i}`} />
            ) : (
              <img src={isClassic ? CLASSIC_IMG : sl.image} alt=""
                   loading={i === 0 ? "eager" : "lazy"}
                   fetchPriority={i === 0 ? "high" : undefined}
                   className={`absolute inset-0 w-full h-full object-cover ${isClassic ? "opacity-35 grayscale" : ""}`} />
            )}
            <div className="absolute inset-0"
                 style={{ background: isClassic
                   ? "linear-gradient(105deg, rgba(0,0,0,0.88), rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.2))"
                   : (ar
                     ? "linear-gradient(270deg, rgba(0,0,0,0.62), rgba(0,0,0,0.28) 45%, rgba(0,0,0,0.05))"
                     : "linear-gradient(90deg, rgba(0,0,0,0.62), rgba(0,0,0,0.28) 45%, rgba(0,0,0,0.05))") }} />
          </div>
        );
      })}

      {/* المحتوى */}
      {classic ? (
        <div className="relative p-5 sm:p-10 text-white min-h-[200px] sm:min-h-[280px] flex flex-col justify-center"
             data-testid="hero-classic">
          <div className="text-[10px] uppercase tracking-[0.3em] mb-1.5 text-white/70">
            {ar ? "RARE GROUP · زهور فاخرة" : "RARE GROUP · LUXURY"}
          </div>
          <h2 className={`${ar ? "font-display-ar" : "font-display-en"} text-2xl sm:text-4xl leading-tight`}
              data-testid="hero-title">
            {ar ? "الورد لغة القلوب" : "Where flowers speak"}
          </h2>
          <p className="mt-1.5 max-w-xl text-white/85 text-[13px]">{t("appTagline")}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/shop" className="btn-gold !min-h-0 !py-2 !px-4 !text-[13px]" data-testid="hero-shop-btn">
              {ar ? "تسوق الآن" : "Shop now"}
            </Link>
            <Link to="/ai"
                  className="btn-outline !border-white !text-white hover:!bg-white hover:!text-black !min-h-0 !py-2 !px-4 !text-[13px]"
                  data-testid="hero-ai-btn">
              <Sparkles size={13} /> {t("tryAI")}
            </Link>
          </div>
        </div>
      ) : (
        <div className="relative min-h-[200px] sm:min-h-[280px] flex flex-col justify-center items-start p-5 sm:p-9 text-white max-w-[75%] sm:max-w-[60%]">
          <h2 className={`${ar ? "font-display-ar" : "font-display-en"} text-2xl sm:text-4xl leading-tight drop-shadow`}
              data-testid="hero-title">
            {ar ? s.title_ar : s.title_en || s.title_ar}
          </h2>
          {(s.subtitle_ar || s.subtitle_en) && (
            <p className="mt-1.5 text-white/90 text-[13px] sm:text-base drop-shadow">{ar ? s.subtitle_ar : s.subtitle_en || s.subtitle_ar}</p>
          )}
          <Link to={s.link || "/shop"}
                className="mt-3.5 inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-bold text-white active:scale-95 transition-transform"
                style={{ background: "#ec5f8d", boxShadow: "0 6px 18px -6px rgba(236,95,141,0.7)" }}
                data-testid="hero-cta">
            {ar ? "تسوق الآن" : "Shop now"}
            <span className={`text-base leading-none ${ar ? "" : "rotate-180"}`}>‹</span>
          </Link>
        </div>
      )}

      {slides.length > 1 && (
        <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5" data-testid="hero-dots">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} aria-label={`slide ${i + 1}`}
                    className="rounded-full transition-all duration-300"
                    style={{ width: i === idx ? 18 : 7, height: 7,
                             background: i === idx ? "#fff" : "rgba(255,255,255,0.55)" }}
                    data-testid={`hero-dot-${i}`} />
          ))}
        </div>
      )}
    </section>
  );
}
